/**
 * ConversationsTab Component
 * Displays conversation list with filtering, stats, and details modal
 * Extracted from ActivityLogs for use in the consolidated SystemLogs page
 */

import { useState, useEffect } from 'react';
import {
  Activity, Search, Download, RefreshCw, ThumbsUp, ThumbsDown,
  User, MessageSquare, X, Minus, CheckCircle, XCircle,
  AlertCircle, ExternalLink, TrendingUp
} from 'react-feather';
import SatisfactionAnalytics from './SatisfactionAnalytics';
import { DateRange, ConversationSummary, SatisfactionResponse } from '../../types/logs';

interface ConversationsTabProps {
  dateRange: DateRange;
}

interface ConversationDetails {
  interactions: Array<{
    id?: string;
    timestamp: string;
    userMessage?: string;
    assistantMessage?: string;
    responseTime?: number;
    confidence?: number;
    metadata?: {
      sourcesUsed?: string[];
    };
  }>;
}

export default function ConversationsTab({ dateRange }: ConversationsTabProps) {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [filteredConversations, setFilteredConversations] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [satisfactionFilter, setSatisfactionFilter] = useState<string>('all');
  const [escalationFilter, setEscalationFilter] = useState<string>('all');
  const [selectedConversation, setSelectedConversation] = useState<ConversationSummary | null>(null);
  const [conversationDetails, setConversationDetails] = useState<ConversationDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchConversations();
  }, [dateRange]);

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.startDate) params.set('startDate', dateRange.startDate.toISOString());
      if (dateRange.endDate) params.set('endDate', dateRange.endDate.toISOString());

      const response = await fetch(`/api/activity-logs?${params}`);
      const data = await response.json();

      if (data.success && data.data) {
        setConversations(data.data.conversations);
        setFilteredConversations(data.data.conversations);
      }
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConversationDetails = async (conversationId: string) => {
    setLoadingDetails(true);
    try {
      const response = await fetch(`/api/activity-logs/conversation/${conversationId}`);
      const data = await response.json();

      if (data.success && data.data) {
        setConversationDetails(data.data);
      }
    } catch (error) {
      console.error('Error fetching conversation details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Apply filters
  useEffect(() => {
    let filtered = conversations;

    if (searchTerm) {
      filtered = filtered.filter(conv =>
        conv.firstUserMessage.toLowerCase().includes(searchTerm.toLowerCase()) ||
        conv.lastAssistantMessage.toLowerCase().includes(searchTerm.toLowerCase()) ||
        conv.conversationId.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (sourceFilter !== 'all') {
      filtered = filtered.filter(conv => conv.contentSource.type === sourceFilter);
    }

    if (satisfactionFilter !== 'all') {
      filtered = filtered.filter(conv => {
        const summary = conv.satisfactionSummary;
        switch (satisfactionFilter) {
          case 'yes': return summary.yesCount > 0;
          case 'partial': return summary.partialCount > 0;
          case 'no': return summary.noCount > 0;
          case 'skip': return summary.skipCount > 0;
          case 'any': return summary.totalResponses > 0;
          default: return true;
        }
      });
    }

    if (escalationFilter === 'escalated') {
      filtered = filtered.filter(conv => conv.escalationSummary.wasEscalated);
    } else if (escalationFilter === 'not-escalated') {
      filtered = filtered.filter(conv => !conv.escalationSummary.wasEscalated);
    }

    setFilteredConversations(filtered);
  }, [searchTerm, sourceFilter, satisfactionFilter, escalationFilter, conversations]);

  const formatTimestamp = (timestamp: string | Date) => {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    return date.toLocaleString();
  };

  const getSatisfactionIcon = (response: SatisfactionResponse) => {
    switch (response) {
      case 'yes': return <CheckCircle className="text-green-500" size={16} />;
      case 'partial': return <AlertCircle className="text-yellow-500" size={16} />;
      case 'no': return <XCircle className="text-red-500" size={16} />;
      case 'skip': return <Minus className="text-gray-400" size={16} />;
      default: return null;
    }
  };

  const getSatisfactionBadge = (summary: ConversationSummary['satisfactionSummary']) => {
    if (summary.totalResponses === 0) return null;

    const percentages = {
      yes: Math.round((summary.yesCount / summary.totalResponses) * 100),
      partial: Math.round((summary.partialCount / summary.totalResponses) * 100),
      no: Math.round((summary.noCount / summary.totalResponses) * 100)
    };

    return (
      <div className="flex items-center space-x-2">
        {percentages.yes > 0 && (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            {percentages.yes}% Yes
          </span>
        )}
        {percentages.partial > 0 && (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            {percentages.partial}% Partial
          </span>
        )}
        {percentages.no > 0 && (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            {percentages.no}% No
          </span>
        )}
      </div>
    );
  };

  const handleExport = () => {
    const csv = [
      ['Conversation ID', 'User Email', 'User Name', 'Organization', 'Timestamp', 'Messages', 'Source', 'Feedback', 'Satisfaction', 'Escalated', 'Response Time (ms)'],
      ...filteredConversations.map(conv => [
        conv.conversationId,
        conv.user?.email || 'Anonymous',
        conv.user?.name || 'Anonymous',
        conv.user?.orgId || 'N/A',
        formatTimestamp(conv.startTime),
        conv.messageCount.toString(),
        conv.contentSource.type,
        `${conv.feedbackSummary.positiveRatings} positive, ${conv.feedbackSummary.negativeRatings} negative`,
        `Yes: ${conv.satisfactionSummary.yesCount}, Partial: ${conv.satisfactionSummary.partialCount}, No: ${conv.satisfactionSummary.noCount}`,
        conv.escalationSummary.wasEscalated ? 'Yes' : 'No',
        Math.round(conv.averageResponseTime || 0).toString()
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conversations-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <RefreshCw className="animate-spin" size={20} />
          <span>Loading conversations...</span>
        </div>
      </div>
    );
  }

  // Calculate stats
  const totalSatisfactionResponses = conversations.reduce((sum, c) => sum + c.satisfactionSummary.totalResponses, 0);
  const totalEscalations = conversations.filter(c => c.escalationSummary.wasEscalated).length;
  const avgSatisfactionRate = totalSatisfactionResponses > 0
    ? Math.round((conversations.reduce((sum, c) => sum + c.satisfactionSummary.yesCount, 0) / totalSatisfactionResponses) * 100)
    : 0;

  const satisfactionData = {
    totalResponses: totalSatisfactionResponses,
    yesCount: conversations.reduce((sum, c) => sum + c.satisfactionSummary.yesCount, 0),
    partialCount: conversations.reduce((sum, c) => sum + c.satisfactionSummary.partialCount, 0),
    noCount: conversations.reduce((sum, c) => sum + c.satisfactionSummary.noCount, 0),
    skipCount: conversations.reduce((sum, c) => sum + c.satisfactionSummary.skipCount, 0),
    escalationCount: totalEscalations
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Conversations</p>
              <p className="text-2xl font-semibold text-gray-900">{conversations.length}</p>
            </div>
            <Activity className="text-blue-500" size={28} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Satisfaction</p>
              <p className="text-2xl font-semibold text-gray-900">{avgSatisfactionRate}%</p>
              <p className="text-xs text-gray-500">{totalSatisfactionResponses} responses</p>
            </div>
            <TrendingUp className="text-green-500" size={28} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Escalations</p>
              <p className="text-2xl font-semibold text-gray-900">{totalEscalations}</p>
              <p className="text-xs text-gray-500">
                {conversations.length > 0 ? Math.round((totalEscalations / conversations.length) * 100) : 0}%
              </p>
            </div>
            <ExternalLink className="text-orange-500" size={28} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Positive</p>
              <p className="text-2xl font-semibold text-gray-900">
                {conversations.reduce((sum, c) => sum + c.feedbackSummary.positiveRatings, 0)}
              </p>
            </div>
            <ThumbsUp className="text-green-500" size={28} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Response</p>
              <p className="text-2xl font-semibold text-gray-900">
                {Math.round(conversations.reduce((sum, c) => sum + (c.averageResponseTime || 0), 0) / conversations.length || 0)}ms
              </p>
            </div>
            <Activity className="text-blue-500" size={28} />
          </div>
        </div>
      </div>

      {/* Satisfaction Analytics */}
      <SatisfactionAnalytics data={satisfactionData} />

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search conversations..."
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Sources</option>
              <option value="direct">Chat</option>
              <option value="widget">Widget</option>
              <option value="api">API</option>
              <option value="teams">Teams</option>
            </select>

            <select
              value={satisfactionFilter}
              onChange={(e) => setSatisfactionFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Satisfaction</option>
              <option value="any">Has Response</option>
              <option value="yes">Fully Satisfied</option>
              <option value="partial">Partially Satisfied</option>
              <option value="no">Not Satisfied</option>
              <option value="skip">Skipped</option>
            </select>

            <select
              value={escalationFilter}
              onChange={(e) => setEscalationFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Conversations</option>
              <option value="escalated">Escalated</option>
              <option value="not-escalated">Not Escalated</option>
            </select>
          </div>

          <div className="flex gap-2">
            <button
              onClick={fetchConversations}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              title="Refresh"
            >
              <RefreshCw size={20} />
            </button>
            <button
              onClick={handleExport}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              title="Export CSV"
            >
              <Download size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Conversations Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Conversation
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Messages
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Feedback
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Satisfaction
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Escalation
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Source
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredConversations.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                  No conversations found
                </td>
              </tr>
            ) : (
              filteredConversations.map((conv) => (
                <tr key={conv.conversationId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {conv.conversationId.substring(0, 8)}...
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatTimestamp(conv.startTime)}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      {conv.user ? (
                        <>
                          <div className="text-sm font-medium text-gray-900">
                            {conv.user.name || 'Unknown'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {conv.user.email || 'No email'}
                          </div>
                        </>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Anonymous</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{conv.messageCount}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {conv.feedbackSummary.positiveRatings > 0 && (
                        <span className="inline-flex items-center">
                          <ThumbsUp className="text-green-500" size={16} />
                          <span className="ml-1 text-sm">{conv.feedbackSummary.positiveRatings}</span>
                        </span>
                      )}
                      {conv.feedbackSummary.negativeRatings > 0 && (
                        <span className="inline-flex items-center">
                          <ThumbsDown className="text-red-500" size={16} />
                          <span className="ml-1 text-sm">{conv.feedbackSummary.negativeRatings}</span>
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getSatisfactionBadge(conv.satisfactionSummary)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {conv.escalationSummary.wasEscalated ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        <ExternalLink size={12} className="mr-1" />
                        {conv.escalationSummary.escalationCount} ticket(s)
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                      {conv.contentSource.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => {
                        setSelectedConversation(conv);
                        fetchConversationDetails(conv.conversationId);
                      }}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Conversation Details Modal */}
      {selectedConversation && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-medium text-gray-900">Conversation Details</h3>
                <button
                  onClick={() => {
                    setSelectedConversation(null);
                    setConversationDetails(null);
                  }}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-4">
                {/* Conversation Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Conversation ID</p>
                    <p className="text-sm text-gray-900">{selectedConversation.conversationId}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Duration</p>
                    <p className="text-sm text-gray-900">
                      {formatTimestamp(selectedConversation.startTime)} - {formatTimestamp(selectedConversation.endTime)}
                    </p>
                  </div>
                  {selectedConversation.user && (
                    <>
                      <div>
                        <p className="text-sm font-medium text-gray-500">User</p>
                        <p className="text-sm text-gray-900">{selectedConversation.user.name || 'Unknown'}</p>
                        {selectedConversation.user.email && (
                          <p className="text-xs text-gray-500">{selectedConversation.user.email}</p>
                        )}
                      </div>
                      {selectedConversation.user.orgId && (
                        <div>
                          <p className="text-sm font-medium text-gray-500">Organization</p>
                          <p className="text-sm text-gray-900">{selectedConversation.user.orgId}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Satisfaction History */}
                {selectedConversation.satisfactionSummary.totalResponses > 0 && selectedConversation.satisfactionSummary.responses?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Satisfaction Responses</h4>
                    <div className="space-y-2">
                      {selectedConversation.satisfactionSummary.responses.map((response, idx) => (
                        <div key={idx} className="flex items-center space-x-2 text-sm">
                          {getSatisfactionIcon(response.response)}
                          <span className="text-gray-600">{formatTimestamp(response.timestamp)}</span>
                          <span className="font-medium">{response.response}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Escalation Details */}
                {selectedConversation.escalationSummary.wasEscalated && selectedConversation.escalationSummary.tickets?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Support Tickets</h4>
                    <div className="space-y-2">
                      {selectedConversation.escalationSummary.tickets.map((ticket) => (
                        <div key={ticket.ticketId} className="border rounded-lg p-3">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="text-sm font-medium">Ticket #{ticket.ticketId}</p>
                              <p className="text-xs text-gray-500">{ticket.userName} ({ticket.userEmail})</p>
                              <p className="text-xs text-gray-500">{formatTimestamp(ticket.timestamp)}</p>
                            </div>
                            <a
                              href={ticket.ticketUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <ExternalLink size={16} />
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Full Conversation */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Full Conversation</h4>
                  {loadingDetails ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="animate-spin mr-2" size={16} />
                      <span className="text-sm text-gray-500">Loading conversation...</span>
                    </div>
                  ) : conversationDetails?.interactions ? (
                    <div className="space-y-4 max-h-96 overflow-y-auto bg-gray-50 p-4 rounded-lg">
                      {conversationDetails.interactions.map((interaction, index) => (
                        <div key={interaction.id || index}>
                          {interaction.userMessage && (
                            <div className="mb-2">
                              <div className="flex items-center mb-1">
                                <User className="text-green-600 mr-2" size={16} />
                                <span className="text-sm font-medium text-green-600">User</span>
                                <span className="text-xs text-gray-500 ml-2">
                                  {formatTimestamp(interaction.timestamp)}
                                </span>
                              </div>
                              <div className="bg-white p-3 rounded border-l-4 border-green-500">
                                <p className="text-sm text-gray-800">{interaction.userMessage}</p>
                              </div>
                            </div>
                          )}

                          {interaction.assistantMessage && (
                            <div className="mb-2">
                              <div className="flex items-center mb-1">
                                <MessageSquare className="text-blue-600 mr-2" size={16} />
                                <span className="text-sm font-medium text-blue-600">Assistant</span>
                                <span className="text-xs text-gray-500 ml-2">
                                  {interaction.responseTime}ms • {Math.round((interaction.confidence || 0) * 100)}% confidence
                                </span>
                              </div>
                              <div className="bg-white p-3 rounded border-l-4 border-blue-500">
                                <div
                                  className="text-sm text-gray-800 prose prose-sm max-w-none"
                                  dangerouslySetInnerHTML={{ __html: interaction.assistantMessage }}
                                />
                                {interaction.metadata?.sourcesUsed && interaction.metadata.sourcesUsed.length > 0 && (
                                  <div className="mt-2 pt-2 border-t border-gray-200">
                                    <p className="text-xs font-medium text-gray-500 mb-1">Sources:</p>
                                    <div className="space-y-1">
                                      {interaction.metadata.sourcesUsed.map((source, idx) => (
                                        <div key={idx}>
                                          <a
                                            href={source}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800 break-all"
                                          >
                                            <ExternalLink size={10} className="mr-1 flex-shrink-0" />
                                            {source}
                                          </a>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-4 max-h-96 overflow-y-auto bg-gray-50 p-4 rounded-lg">
                      <p className="text-xs text-gray-500 mb-4">
                        <i>Full conversation details unavailable. Showing summary:</i>
                      </p>

                      <div>
                        <div className="flex items-center mb-1">
                          <User className="text-green-600 mr-2" size={16} />
                          <span className="text-sm font-medium text-green-600">User</span>
                        </div>
                        <div className="bg-white p-3 rounded border-l-4 border-green-500">
                          <p className="text-sm text-gray-800">{selectedConversation.firstUserMessage}</p>
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center mb-1">
                          <MessageSquare className="text-blue-600 mr-2" size={16} />
                          <span className="text-sm font-medium text-blue-600">Assistant</span>
                        </div>
                        <div className="bg-white p-3 rounded border-l-4 border-blue-500">
                          <div
                            className="text-sm text-gray-800 prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: selectedConversation.lastAssistantMessage }}
                          />
                        </div>
                      </div>

                      {selectedConversation.messageCount > 2 && (
                        <div className="text-center py-2">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                            <AlertCircle size={12} className="mr-1" />
                            {selectedConversation.messageCount - 2} additional messages not shown
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
